import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Drawer, FormControl, FormControlLabel, FormLabel, IconButton,
  ListItemIcon, Menu, MenuItem, Paper, Radio, RadioGroup, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material';
import Layout from '../components/Layout';
import CustomerService from '../services/CustomerService';
import PortalAuthService from '../services/PortalAuthService';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ConfirmModal from './modal/ConfirmModal';
import { useToast } from '../contexts/ToastContext';
import PROJECT_STATUS_OPTIONS from './enums/projectStatusOptionsColors';
import { useTranslation } from 'react-i18next';
import CustomerAtAGlance from './sub-components/CustomerAtAGlance';

const underlineStyle = {
  '& .MuiInput-underline:before': { borderBottomColor: '#dce3ea' },
  '& .MuiInput-underline:hover:before': { borderBottomColor: '#427898' },
  '& .MuiInput-underline:after': { borderBottomColor: '#427898' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#427898' },
};

const WorkspaceDrawer = ({ open, onClose, accentColor = '#427898', title, subtitle, saveLabel, onSave, width = 440, children }) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{ sx: { width: { xs: '100vw', sm: width }, boxShadow: '-4px 0 32px rgba(15,23,42,0.12)' } }}
    slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
  >
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ height: 3, bgcolor: accentColor, flexShrink: 0 }} />
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography fontWeight={700} fontSize={16} color="#0F172A">{title}</Typography>
          {subtitle && <Typography fontSize={12} color="#94A3B8" mt={0.3}>{subtitle}</Typography>}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8', mt: -0.25 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
        {children}
      </Box>
      <Box sx={{ px: 3, py: 2.5, flexShrink: 0, borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B', fontWeight: 500 }}>Cancel</Button>
        <Button variant="contained" onClick={onSave}
          sx={{ bgcolor: accentColor, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', px: 2.5, '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}>
          {saveLabel}
        </Button>
      </Box>
    </Box>
  </Drawer>
);

const SectionHeader = ({ label, count, action }) => (
  <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>
      {label}
      {count != null && count > 0 && (
        <Typography component="span" sx={{ ml: 1, fontSize: 12, color: '#94A3B8', fontWeight: 400 }}>
          ({count})
        </Typography>
      )}
    </Typography>
    {action}
  </Box>
);

const FieldRow = ({ label, value }) => (
  <Box sx={{ py: 1.25 }}>
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 0.4, lineHeight: 1 }}>
      {label}
    </Typography>
    {typeof value === 'string' || value == null
      ? <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1E293B' }}>{value || '—'}</Typography>
      : value}
  </Box>
);

const PORTAL_CHIP_COLOR = { NOT_INVITED: '#94A3B8', INVITED: '#F4A261', ACTIVE: '#2EC4B6' };

const CustomerInfo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { t } = useTranslation('customerInfo');

  const [customer, setCustomer] = useState(null);
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [isEditCustomerOpen, setEditCustomerOpen] = useState(false);
  const [editCustomerData, setEditCustomerData] = useState(null);

  const [isContactModalOpen, setContactModalOpen] = useState(false);
  const [contactData, setContactData] = useState(null);

  const [isConfirmDeleteContactOpen, setConfirmDeleteContactOpen] = useState(false);
  const [isDeletingContact, setIsDeletingContact] = useState(false);
  const [isConfirmDeleteCustomerOpen, setConfirmDeleteCustomerOpen] = useState(false);
  const [isDeletingCustomer, setIsDeletingCustomer] = useState(false);

  const [portalStatus, setPortalStatus] = useState(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteSelectedEmail, setInviteSelectedEmail] = useState('');
  const [inviteCustomEmail, setInviteCustomEmail] = useState('');
  const [isSendingInvite, setIsSendingInvite] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const portalStatusConfig = {
    NOT_INVITED: { label: 'Not invited' },
    INVITED:     { label: 'Invited' },
    ACTIVE:      { label: 'Active' },
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  const fetchCustomer = async () => {
    try {
      const data = await CustomerService.getCustomer(id);
      setCustomer(data.customer);
      setProjects(data.projects || []);
      setContacts(data.customerContact || []);
      setEditCustomerData(data.customer);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
    }
  };

  const fetchPortalStatus = async () => {
    if (!id) return;
    try {
      const data = await PortalAuthService.getPortalStatus(id);
      setPortalStatus(data);
    } catch (error) {
      console.error('Failed to fetch portal status:', error);
    }
  };

  useEffect(() => {
    fetchCustomer();
    fetchPortalStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Validation ──────────────────────────────────────────────────────────────

  const validateInputs = () => {
    const errors = {};
    const letterPattern = /^[A-Za-z\s]+$/;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phonePattern = /^\+?[0-9]+$/;
    const numberPattern = /^[0-9]+$/;
    const isEmpty = (v) => !v || v.toString().trim() === '';

    if (isEmpty(editCustomerData?.name)) errors.name = t('validation.companyNameRequired');
    else if (!letterPattern.test(editCustomerData.name)) errors.name = t('validation.companyNameLettersOnly');
    if (isEmpty(editCustomerData?.email)) errors.email = t('validation.emailRequired');
    else if (!emailPattern.test(editCustomerData.email)) errors.email = t('validation.emailInvalid');
    if (!isEmpty(editCustomerData?.phone) && !phonePattern.test(editCustomerData.phone)) errors.phone = t('validation.phoneInvalid');
    if (isEmpty(editCustomerData?.employeesNr)) errors.employeesNr = t('validation.employeesNrRequired');
    else if (!numberPattern.test(editCustomerData.employeesNr.toString())) errors.employeesNr = t('validation.employeesNrInvalid');
    if (isEmpty(editCustomerData?.foundationYear)) errors.foundationYear = t('validation.foundationYearRequired');
    else if (!numberPattern.test(editCustomerData.foundationYear.toString())) errors.foundationYear = t('validation.foundationYearInvalid');
    if (isEmpty(editCustomerData?.address)) errors.address = t('validation.addressRequired');

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCustomerContactInputs = () => {
    const errors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phonePattern = /^\+?[0-9]+$/;
    const isEmpty = (v) => !v || v.toString().trim() === '';

    if (isEmpty(contactData?.firstName)) errors.firstName = t('validation.firstNameRequired');
    if (isEmpty(contactData?.lastName)) errors.lastName = t('validation.lastNameRequired');
    if (isEmpty(contactData?.email)) errors.email = t('validation.emailRequired');
    else if (!emailPattern.test(contactData.email)) errors.email = t('validation.emailInvalid');
    if (!isEmpty(contactData?.phone) && !phonePattern.test(contactData.phone)) errors.phone = t('validation.phoneInvalid');

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleEditCustomer = async () => {
    if (!editCustomerData || !validateInputs()) return;
    try {
      await CustomerService.updateCustomer(id, editCustomerData);
      fetchCustomer();
      setEditCustomerOpen(false);
      showToast(t('toasts.customerUpdated'), 'success');
    } catch {
      showToast(t('toasts.customerUpdateError'), 'error');
    }
  };

  const handleDeleteCustomer = () => setConfirmDeleteCustomerOpen(true);

  const handleConfirmDeleteCustomer = async () => {
    setIsDeletingCustomer(true);
    try {
      await CustomerService.deleteCustomer(customer.id);
      navigate('/customers');
    } catch {
      setIsDeletingCustomer(false);
    }
  };

  const handleOpenContactModal = (contact = null) => {
    setContactData(contact);
    setValidationErrors({});
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setContactData(null);
  };

  const handleSaveContact = async () => {
    try {
      contactData.customer = customer.id;
      if (!validateCustomerContactInputs()) return;
      let data;
      if (contactData.id) {
        data = await CustomerService.updateContact(contactData.id, contactData);
        setContacts(contacts.map((c) => (c.id === contactData.id ? data : c)));
        showToast(t('toasts.contactUpdated'), 'success');
      } else {
        data = await CustomerService.createContact(contactData);
        setContacts([...contacts, data]);
        showToast(t('toasts.contactCreated'), 'success');
      }
      handleCloseContactModal();
    } catch {
      console.error('Error saving contact');
    }
  };

  const handleDeleteContact = (contact) => {
    setContactData(contact);
    setConfirmDeleteContactOpen(true);
  };

  const handleConfirmDeleteContact = async () => {
    setIsDeletingContact(true);
    try {
      await CustomerService.deleteContact(contactData.id);
      setContacts((prev) => prev.filter((c) => c.id !== contactData.id));
      setConfirmDeleteContactOpen(false);
      showToast(t('toasts.contactDeleted'), 'warning');
    } catch {
      showToast(t('toasts.contactDeleteError'), 'error');
    } finally {
      setIsDeletingContact(false);
    }
  };

  const handleOpenInviteModal = () => {
    setInviteSelectedEmail(customer?.email || '__custom__');
    setInviteCustomEmail('');
    setInviteModalOpen(true);
  };

  const handleSendInvite = async () => {
    const email = inviteSelectedEmail === '__custom__' ? inviteCustomEmail.trim() : inviteSelectedEmail;
    if (!email) { showToast('Please select or enter an email address.', 'error'); return; }
    setIsSendingInvite(true);
    try {
      await PortalAuthService.invite({ customerId: id, email });
      setInviteModalOpen(false);
      showToast('Invite sent successfully.', 'success');
      fetchPortalStatus();
    } catch {
      showToast('Failed to send invite. Please try again.', 'error');
    } finally {
      setIsSendingInvite(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const portalSt = portalStatus?.portalStatus || 'NOT_INVITED';
  const portalColor = PORTAL_CHIP_COLOR[portalSt] || '#94A3B8';
  const portalLabel = portalStatusConfig[portalSt]?.label || portalSt;

  return (
    <Layout fullBleed>
      <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>

        {/* ── Sticky header ── */}
        <Box sx={{
          bgcolor: '#fff', borderBottom: '1px solid #EEF2F6',
          px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2,
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
            onClick={() => navigate('/customers')}
            sx={{ color: '#64748B', fontWeight: 500, textTransform: 'none', fontSize: 13, px: 1, minWidth: 0, '&:hover': { bgcolor: '#F1F5F9' } }}
          >
            {t('sections.customers')}
          </Button>

          <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />

          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>
            {customer?.name || '—'}
          </Typography>

          <Chip
            label={portalLabel}
            size="small"
            sx={{ bgcolor: portalColor + '18', color: portalColor, fontWeight: 600, fontSize: 11, border: `1px solid ${portalColor}30`, height: 22 }}
          />

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={t('buttons.editCustomer')}>
              <IconButton size="small" onClick={() => setEditCustomerOpen(true)}
                sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="More actions">
              <IconButton size="small" onClick={e => setMenuAnchorEl(e.currentTarget)}
                sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                <MoreVertIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={() => setMenuAnchorEl(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{ elevation: 0, sx: { borderRadius: 2, minWidth: 200, border: '1px solid #E8EDF2', boxShadow: '0 8px 24px rgba(0,0,0,0.10)', mt: 0.5 } }}
            >
              <MenuItem onClick={() => { setEditCustomerOpen(true); setMenuAnchorEl(null); }} dense>
                <ListItemIcon><EditIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
                {t('buttons.editCustomer')}
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem onClick={() => { handleDeleteCustomer(); setMenuAnchorEl(null); }} dense sx={{ color: '#EF4444' }}>
                <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'inherit' }} /></ListItemIcon>
                {t('buttons.deleteCustomer')}
              </MenuItem>
            </Menu>
          </Stack>
        </Box>

        {/* ── Body ── */}
        <Box sx={{
          display: 'flex', gap: 3, p: { xs: 2, md: 3 },
          maxWidth: 1280, mx: 'auto', alignItems: 'flex-start',
          flexDirection: { xs: 'column', md: 'row' },
        }}>

          {/* ── Main column ── */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Customer info */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
              <SectionHeader label={t('sections.customerInfo')} />
              <Box sx={{ px: 3, py: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 6 }}>
                <FieldRow label={t('labels.email')}         value={customer?.email} />
                <FieldRow label={t('labels.phone')}         value={customer?.phone} />
                <FieldRow label={t('labels.address')}       value={customer?.address} />
                <FieldRow label={t('labels.employeesNr')}   value={customer?.employeesNr?.toString()} />
                <FieldRow label={t('labels.foundationYear')} value={customer?.foundationYear?.toString()} />
              </Box>
            </Paper>

            {/* Contacts */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
              <SectionHeader
                label={t('sections.contacts')}
                count={contacts.length}
                action={
                  <Button
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={() => handleOpenContactModal()}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13, color: '#427898', px: 1.5, '&:hover': { bgcolor: '#F0F7FF' } }}
                  >
                    {t('buttons.createContact')}
                  </Button>
                }
              />
              <Box sx={{ p: 2.5 }}>
                {contacts.length === 0 ? (
                  <Box
                    onClick={() => handleOpenContactModal()}
                    sx={{
                      border: '1.5px dashed #CBD5E1', borderRadius: 2, py: 4, textAlign: 'center', cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: '#427898', bgcolor: '#F8FBFF' },
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: '#94A3B8', mb: 0.5 }}>No contacts yet</Typography>
                    <Typography sx={{ fontSize: 12, color: '#CBD5E1' }}>Click to add the first contact</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                    {contacts.map((contact) => {
                      const initials = `${contact.firstName?.[0] || ''}${contact.lastName?.[0] || ''}`.toUpperCase();
                      const hue = [...(contact.firstName || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
                      return (
                        <Paper key={contact.id} elevation={0} sx={{
                          border: '1px solid #EEF2F6', borderRadius: 1.5, p: 2,
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          transition: 'border-color 0.15s, box-shadow 0.15s',
                          '&:hover': { borderColor: '#CBD5E1', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
                          '&:hover .contact-actions': { opacity: 1 },
                          '& .contact-actions': { opacity: 0, transition: 'opacity 0.15s' },
                        }}>
                          <Box sx={{
                            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                            bgcolor: `hsl(${hue}, 55%, 88%)`, color: `hsl(${hue}, 55%, 32%)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 13, fontWeight: 700,
                          }}>
                            {initials}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {contact.firstName} {contact.lastName}
                            </Typography>
                            {contact.email && (
                              <Typography sx={{ fontSize: 12, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {contact.email}
                              </Typography>
                            )}
                            {contact.phone && (
                              <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>{contact.phone}</Typography>
                            )}
                          </Box>
                          <Stack className="contact-actions" direction="row" spacing={0.25}>
                            <IconButton size="small" onClick={() => handleOpenContactModal(contact)}
                              sx={{ color: '#94A3B8', '&:hover': { color: '#427898', bgcolor: '#F0F7FF' } }}>
                              <EditIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteContact(contact)}
                              sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}>
                              <DeleteIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Projects */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
              <SectionHeader label={t('sections.projects')} count={projects.length} />
              {projects.length === 0 ? (
                <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
                  <Typography sx={{ color: '#CBD5E1', fontSize: 13 }}>No projects yet</Typography>
                </Box>
              ) : projects.map((project, i) => {
                const sc = PROJECT_STATUS_OPTIONS.find(o => o.label === project?.status)?.color || '#94A3B8';
                return (
                  <Box
                    key={project.id || i}
                    onClick={() => navigate('/project-info/' + project.id)}
                    sx={{
                      px: 3, py: 1.75, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
                      borderBottom: i < projects.length - 1 ? '1px solid #F1F5F9' : 'none',
                      transition: 'background 0.1s',
                      '&:hover': { bgcolor: '#FAFBFC' },
                      '&:hover .proj-arrow': { opacity: 1 },
                      '& .proj-arrow': { opacity: 0, transition: 'opacity 0.15s' },
                    }}
                  >
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: sc, flexShrink: 0 }} />
                    <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {project.name}
                    </Typography>
                    <Chip
                      label={project.status}
                      size="small"
                      sx={{ bgcolor: sc + '18', color: sc, fontWeight: 600, fontSize: 11, border: `1px solid ${sc}30`, height: 20 }}
                    />
                    <Typography sx={{ fontSize: 12, color: '#94A3B8', whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>
                      {fmtDate(project.startDate)}{project.endDate ? ` → ${fmtDate(project.endDate)}` : ''}
                    </Typography>
                    <ArrowForwardIcon className="proj-arrow" sx={{ fontSize: 14, color: '#CBD5E1', flexShrink: 0 }} />
                  </Box>
                );
              })}
            </Paper>
          </Box>

          {/* ── Right sidebar ── */}
          <Box sx={{ width: { md: 272 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, position: { md: 'sticky' }, top: { md: 72 } }}>

            {/* At a Glance */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 2.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                {t('sections.atAGlance')}
              </Typography>
              <CustomerAtAGlance customerId={id} />
            </Paper>

            {/* Customer Portal */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 2.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                Customer Portal
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: portalSt !== 'ACTIVE' ? 2 : 0 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: portalColor, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1E293B' }}>{portalLabel}</Typography>
              </Stack>
              {portalSt !== 'ACTIVE' && (
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={handleOpenInviteModal}
                  sx={{ borderColor: '#CBD5E1', color: '#475569', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, '&:hover': { borderColor: '#427898', color: '#427898', bgcolor: '#F0F7FF' } }}
                >
                  {portalSt === 'INVITED' ? 'Re-invite to portal' : 'Invite to portal'}
                </Button>
              )}
            </Paper>
          </Box>
        </Box>

        {/* ── Edit Customer Dialog ── */}
        <Dialog open={isEditCustomerOpen} onClose={() => setEditCustomerOpen(false)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}>
          <DialogTitle sx={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5 }}>
            {t('sections.editCustomerInfo')}
            <IconButton size="small" onClick={() => setEditCustomerOpen(false)}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 0.5 }}>
              <TextField label={t('labels.companyName')} fullWidth value={editCustomerData?.name || ''}
                onChange={e => setEditCustomerData({ ...editCustomerData, name: e.target.value })}
                error={!!validationErrors.name} helperText={validationErrors.name} />
              <TextField label={t('labels.email')} fullWidth value={editCustomerData?.email || ''}
                onChange={e => setEditCustomerData({ ...editCustomerData, email: e.target.value })}
                error={!!validationErrors.email} helperText={validationErrors.email} />
              <TextField label={t('labels.phone')} fullWidth value={editCustomerData?.phone || ''}
                onChange={e => setEditCustomerData({ ...editCustomerData, phone: e.target.value })}
                error={!!validationErrors.phone} helperText={validationErrors.phone} />
              <TextField label={t('labels.employeesNr')} fullWidth type="number" value={editCustomerData?.employeesNr || ''}
                onChange={e => setEditCustomerData({ ...editCustomerData, employeesNr: e.target.value })}
                error={!!validationErrors.employeesNr} helperText={validationErrors.employeesNr} />
              <TextField label={t('labels.foundationYear')} fullWidth type="number" value={editCustomerData?.foundationYear || ''}
                onChange={e => setEditCustomerData({ ...editCustomerData, foundationYear: e.target.value })}
                error={!!validationErrors.foundationYear} helperText={validationErrors.foundationYear} />
              <TextField label={t('labels.address')} fullWidth value={editCustomerData?.address || ''}
                onChange={e => setEditCustomerData({ ...editCustomerData, address: e.target.value })}
                error={!!validationErrors.address} helperText={validationErrors.address} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setEditCustomerOpen(false)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleEditCustomer}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}>
              {t('buttons.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Add / Edit Contact Drawer ── */}
        <WorkspaceDrawer
          open={isContactModalOpen}
          onClose={handleCloseContactModal}
          accentColor="#427898"
          title={contactData?.id ? t('modals.contactTitleEdit') : t('modals.contactTitleCreate')}
          subtitle={t('sections.contacts')}
          saveLabel={t('buttons.save')}
          onSave={handleSaveContact}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField fullWidth size="small" label={t('labels.firstName')} autoFocus
                value={contactData?.firstName || ''}
                onChange={e => setContactData({ ...contactData, firstName: e.target.value })}
                error={!!validationErrors.firstName} helperText={validationErrors.firstName || ' '} />
              <TextField fullWidth size="small" label={t('labels.lastName')}
                value={contactData?.lastName || ''}
                onChange={e => setContactData({ ...contactData, lastName: e.target.value })}
                error={!!validationErrors.lastName} helperText={validationErrors.lastName || ' '} />
            </Box>
            <TextField fullWidth size="small" label={t('labels.email')} type="email"
              value={contactData?.email || ''}
              onChange={e => setContactData({ ...contactData, email: e.target.value })}
              error={!!validationErrors.email} helperText={validationErrors.email || ' '} />
            <TextField fullWidth size="small" label={t('labels.phone')}
              value={contactData?.phone || ''}
              onChange={e => setContactData({ ...contactData, phone: e.target.value })}
              error={!!validationErrors.phone} helperText={validationErrors.phone || ' '} />
            <TextField fullWidth size="small" label={t('labels.address')}
              value={contactData?.address || ''}
              onChange={e => setContactData({ ...contactData, address: e.target.value })}
              helperText=" " />
          </Box>
        </WorkspaceDrawer>

        {/* ── Confirm modals ── */}
        <ConfirmModal
          open={isConfirmDeleteContactOpen}
          onClose={() => !isDeletingContact && setConfirmDeleteContactOpen(false)}
          onConfirm={handleConfirmDeleteContact}
          question={t('confirm.deleteContactQuestion', { name: `${contactData?.firstName || ''} ${contactData?.lastName || ''}` })}
          isLoading={isDeletingContact}
        />
        <ConfirmModal
          open={isConfirmDeleteCustomerOpen}
          onClose={() => !isDeletingCustomer && setConfirmDeleteCustomerOpen(false)}
          onConfirm={handleConfirmDeleteCustomer}
          question={t('confirm.deleteCustomerQuestion', { name: customer?.name || '' })}
          isLoading={isDeletingCustomer}
        />

        {/* ── Invite to portal dialog ── */}
        <Dialog
          open={inviteModalOpen}
          onClose={() => !isSendingInvite && setInviteModalOpen(false)}
          maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
        >
          <Box sx={{ height: 4, bgcolor: '#427898', flexShrink: 0 }} />
          <DialogTitle sx={{ fontWeight: 700, color: '#1a2332', pb: 1 }}>
            Invite customer to portal
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
              An invite email will be sent to the selected address. The customer will receive a link to set up their account.
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                Send invite to
              </FormLabel>
              <RadioGroup value={inviteSelectedEmail} onChange={e => setInviteSelectedEmail(e.target.value)}>
                {customer?.email && (
                  <FormControlLabel
                    value={customer.email}
                    control={<Radio size="small" sx={{ color: '#427898', '&.Mui-checked': { color: '#427898' } }} />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{customer.email}</Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>Customer email</Typography>
                      </Box>
                    }
                  />
                )}
                {contacts?.filter(c => c.email)?.map(contact => (
                  <FormControlLabel
                    key={contact.id || contact.email}
                    value={contact.email}
                    control={<Radio size="small" sx={{ color: '#427898', '&.Mui-checked': { color: '#427898' } }} />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{contact.email}</Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>{contact.firstName} {contact.lastName}</Typography>
                      </Box>
                    }
                  />
                ))}
                <FormControlLabel
                  value="__custom__"
                  control={<Radio size="small" sx={{ color: '#427898', '&.Mui-checked': { color: '#427898' } }} />}
                  label={<Typography variant="body2" fontWeight={500}>Use a different email</Typography>}
                />
              </RadioGroup>
              {inviteSelectedEmail === '__custom__' && (
                <TextField
                  fullWidth variant="standard" label="Email address" type="email" autoFocus
                  value={inviteCustomEmail}
                  onChange={e => setInviteCustomEmail(e.target.value)}
                  sx={{ mt: 1, ...underlineStyle }}
                />
              )}
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setInviteModalOpen(false)} disabled={isSendingInvite}
              sx={{ textTransform: 'none', color: '#64748b' }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSendInvite} disabled={isSendingInvite}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}>
              {isSendingInvite ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Send invite'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Layout>
  );
};

export default CustomerInfo;
